// tokens.ts — typed JS access to the BlackDice palette + scale.
//
// The runtime source of truth is blackdice.css (CSS custom properties). These
// exports mirror the *brand* values for use in canvas drawing, charts, emails,
// or anywhere you can't read a CSS var. Theme-dependent tokens (surfaces, text)
// are intentionally NOT duplicated here — read them live with
// getComputedStyle(el).getPropertyValue('--text') so they track light/dark.

import type { BDAccent } from './types';

/** Raw brand palette — stable across themes. */
export const palette = {
  teal900: '#02272f',
  teal800: '#013B49',
  teal700: '#054b5b',
  teal600: '#0a4856',
  teal500: '#135462',
  slate:   '#33414A',
  mint700: '#1d6e58',
  mint500: '#3BB586',
  mint400: '#4ec99a',
  mint300: '#6bd6ad',
  mint200: '#a8e9cb',
  amber:   '#ffab40',
  red:     '#ff5d5d',
  blue:    '#4ea4f4',
  violet:  '#9d7af2',
} as const;

/** Accent ramps keyed by the [data-accent] value. { accent, accent2, soft, glow } */
export const accents: Record<BDAccent, { accent: string; accent2: string; soft: string; glow: string }> = {
  mint:   { accent: '#3BB586', accent2: '#6bd6ad', soft: 'rgba(59,181,134,0.16)',  glow: 'rgba(59,181,134,0.30)'  },
  blue:   { accent: '#3d8bf2', accent2: '#6fa9f7', soft: 'rgba(61,139,242,0.16)',  glow: 'rgba(61,139,242,0.30)'  },
  violet: { accent: '#9d7af2', accent2: '#b8a0f7', soft: 'rgba(157,122,242,0.16)', glow: 'rgba(157,122,242,0.30)' },
  amber:  { accent: '#f2a93d', accent2: '#f7c574', soft: 'rgba(242,169,61,0.16)',  glow: 'rgba(242,169,61,0.30)'  },
};

/** Font stacks (match the CSS --bd-font / --bd-mono). Ubuntu is loaded via the stylesheet @import. */
export const fonts = {
  sans: "'Ubuntu', -apple-system, system-ui, sans-serif",
  mono: "'Ubuntu Mono', ui-monospace, monospace",
} as const;

/** Corner radii used across cards, chips, controls. */
export const radius = {
  chip: 100,   // fully-round pills/chips
  control: 9,  // inputs, small buttons
  card: 18,    // cards / surfaces
  cta: 16,     // primary CTA buttons
} as const;

/** The full list of CSS custom properties resolved per theme (for reference / IntelliSense). */
export const cssVarNames = [
  '--bg', '--bg-grad', '--hex-stroke', '--hex-dot',
  '--surface', '--surface-2', '--surface-3', '--surface-strong',
  '--border', '--border-strong',
  '--text', '--text-2', '--text-mute', '--text-dim',
  '--accent', '--accent-2', '--accent-soft', '--accent-glow',
  '--danger', '--danger-soft', '--warn', '--warn-soft', '--info',
  '--shadow-card', '--tab-bg', '--status', '--bd-font', '--bd-mono',
] as const;

export type CSSVarName = typeof cssVarNames[number];

/** Read a live theme token from an element (defaults to :root). */
export function readToken(name: CSSVarName, el: Element | null = typeof document !== 'undefined' ? document.documentElement : null): string {
  if (!el) return '';
  return getComputedStyle(el).getPropertyValue(name).trim();
}
